import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { safeLocalGet, safeLocalSet } from "@/utils/safeStorage";
import { ANALYTICS_CONSENT_KEY, createEventId } from "@/utils/analytics";

import { captureAttribution, getAttribution } from "@/utils/attribution";

const AUTO_COLLAPSE_MS = 10000;
// Railway injects this at build time. Never reuse a production measurement ID in staging.
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID?.trim();
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID?.trim();

const STANDARD_EVENT_NAMES = {
  page_view: { ga: 'page_view', meta: 'PageView' },
  landing_view: { ga: 'page_view', meta: 'PageView' },
  resume_product_viewed: { ga: 'view_item', meta: 'ViewContent' },
  signup_completed: { ga: 'sign_up', meta: 'CompleteRegistration' },
  checkout_started: { ga: 'begin_checkout', meta: 'InitiateCheckout' },
  purchase_completed: { ga: 'purchase', meta: 'Purchase' },
};

function removeGoogleCookies() {
  document.cookie.split(";").forEach((part) => {
    const name = part.split("=")[0]?.trim();
    if (name?.startsWith("_ga")) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    }
  });
}

function enableAnalytics() {
  if (window.__resumeReadyAnalyticsEnabled || (!GA_MEASUREMENT_ID && !META_PIXEL_ID)) return;
  window.__resumeReadyAnalyticsEnabled = true;

  if (GA_MEASUREMENT_ID) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
      window.dataLayer.push(arguments);
    };

    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

    if (!document.getElementById("resumeready-ga4")) {
      const script = document.createElement("script");

      script.id = "resumeready-ga4";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }
  }

  if (META_PIXEL_ID) {
    window.fbq = window.fbq || function fbq() {
      window.fbq.callMethod
        ? window.fbq.callMethod.apply(window.fbq, arguments)
        : window.fbq.queue.push(arguments);
    };
    window._fbq = window._fbq || window.fbq;
    window.fbq.push = window.fbq;
    window.fbq.loaded = true;
    window.fbq.version = '2.0';
    window.fbq.queue = window.fbq.queue || [];
    window.fbq('consent', 'grant');
    window.fbq('init', META_PIXEL_ID);

    if (!document.getElementById('resumeready-meta-pixel')) {
      const script = document.createElement('script');
      script.id = 'resumeready-meta-pixel';
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
  }

  window.trackConversion = (eventName, eventParams = {}) => {
    const { event_id: suppliedEventId, ...safeParams } = eventParams || {};
    const eventId = suppliedEventId || createEventId(eventName);
    const standard = STANDARD_EVENT_NAMES[eventName];
    const params = { ...getAttribution(), ...safeParams, resumeready_event_name: eventName };

    window.gtag?.("event", standard?.ga || eventName, params);
    if (standard?.meta) {
      window.fbq?.('track', standard.meta, params, { eventID: eventId });

    } else {
      window.fbq?.('trackCustom', eventName, params, { eventID: eventId });
    }
    return eventId;
  };

  captureAttribution();
  const pagePath = window.location.pathname || '/';
  window.trackConversion(pagePath === '/' ? 'landing_view' : 'page_view', { page_path: pagePath });
  window.__resumeReadyLastPageViewPath = pagePath;

}
function disableAnalytics() {
  window.gtag?.("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.fbq?.('consent', 'revoke');
  window.trackConversion = undefined;
  window.__resumeReadyAnalyticsEnabled = false;
  removeGoogleCookies();
  ['_fbp', '_fbc'].forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  });
}

export default function AnalyticsConsentBanner() {
  const { api, user } = useAuth();
  const [choice, setChoice] = useState(null);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const savedChoice = safeLocalGet(ANALYTICS_CONSENT_KEY);
    setChoice(savedChoice);
    setOpen(!savedChoice);

    if (savedChoice === "granted") enableAnalytics();
    if (savedChoice === "denied") disableAnalytics();

    if (savedChoice) return undefined;
    const timer = window.setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const choose = (nextChoice) => {
    safeLocalSet(ANALYTICS_CONSENT_KEY, nextChoice);
    setChoice(nextChoice);
    setOpen(false);
    setCollapsed(false);

    if (nextChoice === "granted") enableAnalytics();
    else {
      disableAnalytics();
      if (user?.id) api.post('/users/tracking-opt-out', {}, { timeout: 5000 })
        .catch(() => toast.error('Could not update server tracking. Please retry cookie settings.'));
    }
  };

  const reopen = () => {
    setOpen(true);
    setCollapsed(false);
  };

  if ((!open && choice) || (open && collapsed)) {
    return (
      <button
        type="button"
        onClick={reopen}
        className="fixed bottom-3 left-3 z-[100] rounded-full border border-border bg-background/95 px-3 py-2 text-xs font-medium text-muted-foreground shadow-md backdrop-blur hover:text-foreground"
        aria-label="Open cookie settings"
      >
        Cookies
      </button>
    );
  }

  if (!open) return null;

  return (
    <section
      role="dialog"
      aria-label="Cookie choices"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-sm rounded-lg border border-border bg-background/95 p-3 shadow-xl backdrop-blur md:inset-x-auto md:right-4 md:w-[360px]"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Cookie choices</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Essential storage keeps you signed in. Optional tracking shares site activity with Google and Meta to measure visits and ads. No CV text is sent to either.{" "}
            <Link to="/privacy" className="underline underline-offset-2">Details</Link>
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="shrink-0 rounded px-1.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Minimise cookie choices"
        >
          Later
        </button>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => choose("denied")}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          Essential only
        </button>
        <button
          type="button"
          onClick={() => choose("granted")}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
        >
          Allow optional tracking
        </button>
      </div>
    </section>
  );
}

